import { useState } from 'react';
import { Dialog, DialogDescription, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { DialogContentSafeArea } from '@/components/ui/dialog-safe-area';
import { Button } from '@/components/ui/button';
import { Copy, ExternalLink, Link, Mail, MessageSquare } from 'lucide-react';
import type { EmailData, ExportData, ExportType } from '../../types';
import { KontentinoPromoCard } from '../KontentinoPromoCard';
import { useOpenAIActions } from 'react-openai-apps-sdk';

interface ExportOptionsDialogProps {
  open: boolean;
  onClose: () => void;
  exportType: ExportType | null;
  exportData: ExportData | null;
  emailData: EmailData | null;
  uploadedUrl: string | null;
  onShowPreview: () => void; // Kept for compatibility (Preview button is commented out)
  onSendViaEmail: () => void;
}

export function ExportOptionsDialog({
  open,
  onClose,
  exportType,
  exportData,
  emailData,
  uploadedUrl,
  onShowPreview: _onShowPreview,
  onSendViaEmail,
}: ExportOptionsDialogProps) {
  const [copiedMessage, setCopiedMessage] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { sendFollowUpMessage } = useOpenAIActions();

  const handleShareInChatOnly = async () => {
    if (!uploadedUrl || !exportData) return;

    const fileTypeLabels: Record<string, string> = {
      pdf: 'PDF file',
      png: 'calendar image',
      csv: 'CSV file',
    };
    const fileTypeLabel = exportType ? fileTypeLabels[exportType] || 'file' : 'file';

    const message = `✅ Here's your generated ${fileTypeLabel} for the Content Calendar:\n\n[Download ${exportData.filename}](${uploadedUrl})`;

    await sendFollowUpMessage({
      prompt: message,
      fallbackMessage: 'ChatGPT API not available. Try copying the URL instead.',
      onSuccess: () => {
        setCopiedMessage('Shared in chat!');
        setTimeout(() => {
          setCopiedMessage(null);
          onClose();
        }, 1500);
      },
      onError: (error) => {
        console.error('Share in chat failed:', error);
        setCopiedMessage('Failed to share. Try copying the URL instead.');
        setTimeout(() => setCopiedMessage(null), 3000);
      },
    });
  };

  const getExportTitle = () => {
    const titles: Record<string, string> = {
      pdf: 'Your PDF is Ready!',
      png: 'Your Calendar Image is Ready!',
      csv: 'Your CSV is Ready!',
      email: 'Send Calendar for Approval',
      'calendar-link': 'Your Shareable Link is Ready!'
    };
    return exportType ? titles[exportType] || 'Your Export is Ready!' : 'Your Export is Ready!';
  };

  const getExportDescription = () => {
    const descriptions: Record<string, string> = {
      pdf: 'Choose how you\'d like to share your calendar PDF',
      png: 'Choose how you\'d like to share your calendar image',
      csv: 'Choose how you\'d like to share your calendar data',
      email: 'Send this calendar to your team for approval',
      'calendar-link': 'Share this interactive calendar with anyone (expires in 7 days)'
    };
    return exportType ? descriptions[exportType] || 'Choose how you\'d like to share it' : 'Choose how you\'d like to share it';
  };

  if (!exportData && !emailData) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContentSafeArea className="!max-w-md max-h-[85vh] overflow-y-auto [scrollbar-gutter:stable]">
        <DialogHeader>
          <DialogTitle>{getExportTitle()}</DialogTitle>
          <DialogDescription>
            {getExportDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {exportType === 'calendar-link' && exportData?.text ? (
            <div className="space-y-3">
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Link className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm mb-1">Shareable Calendar Link</h3>
                    <p className="text-xs text-muted-foreground mb-2">
                      Anyone with this link can view the calendar (read-only)
                    </p>
                    <div className="bg-background p-2 rounded border text-xs font-mono break-all">
                      {exportData.text}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(exportData.text || '');
                    setCopiedMessage('Link copied to clipboard!');
                    setTimeout(() => setCopiedMessage(null), 2000);
                  }}
                  variant="default"
                  size="lg"
                  className="w-full justify-start gap-3"
                >
                  <Copy className="h-5 w-5" />
                  <span>Copy Link</span>
                </Button>
                <Button
                  onClick={() => {
                    const newWindow = window.open(exportData.text, '_blank');
                    if (!newWindow) {
                      console.error('Popup blocked - enable popups');
                    } else {
                      setCopiedMessage('Opening calendar...');
                      setTimeout(() => setCopiedMessage(null), 2000);
                    }
                  }}
                  variant="outline"
                  size="lg"
                  className="w-full justify-start gap-3"
                >
                  <ExternalLink className="h-5 w-5" />
                  <span>Open Calendar</span>
                </Button>
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground text-center">
                  ⏱️ This link will expire in 7 days
                </p>
              </div>
            </div>
          ) : uploadedUrl ? (
            <div className="space-y-3">
              <Button
                onClick={() => {
                  onSendViaEmail();
                  setCopiedMessage('Opening email...');
                  setTimeout(() => setCopiedMessage(null), 2000);
                }}
                variant="default"
                size="lg"
                className="w-full justify-start gap-3"
              >
                <Mail className="h-5 w-5" />
                <span>Send via Email</span>
              </Button>
              <Button
                onClick={() => {
                  handleShareInChatOnly();
                }}
                variant="outline"
                size="lg"
                className="w-full justify-start gap-3"
              >
                <MessageSquare className="h-5 w-5" />
                <span>Share with ChatGPT</span>
              </Button>
              <Button
                onClick={() => {
                  const newWindow = window.open(uploadedUrl, '_blank');
                  if (!newWindow) {
                    console.error('Popup blocked - enable popups');
                  } else {
                    setCopiedMessage('Opening file...');
                    setTimeout(() => setCopiedMessage(null), 2000);
                  }
                }}
                variant="outline"
                size="lg"
                className="w-full justify-start gap-3"
              >
                <ExternalLink className="h-5 w-5" />
                <span>Open in Browser</span>
              </Button>

              <div className="space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  {showAdvanced ? '▼' : '▶'} Having trouble? Get direct link
                </Button>
                {showAdvanced && (
                  <div className="space-y-2 pt-2">
                    <p className="text-xs text-muted-foreground">
                      Copy this URL and paste it into a new browser tab:
                    </p>
                    <div className="relative">
                      <div className="text-xs bg-muted p-3 rounded-md font-mono break-all border">
                        {uploadedUrl}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute top-1 right-1"
                        onClick={() => {
                          navigator.clipboard.writeText(uploadedUrl);
                          setCopiedMessage('Link copied!');
                          setTimeout(() => setCopiedMessage(null), 2000);
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <KontentinoPromoCard className="mt-6" />
        </div>

        {copiedMessage && (
          <div className="flex items-center justify-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-950 p-3 rounded-md">
            <span className="text-lg">✓</span>
            <span>{copiedMessage}</span>
          </div>
        )}
      </DialogContentSafeArea>
    </Dialog>
  );
}
